const React = require("react");
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { PageHeader, Button, Kpi } = require("../components/ui");
const { CJSyncActionModal } = require("../components/CJSyncActionModal");
const { createCJSyncService } = require("../services/cjDropshippingSyncService");
const { PASSWORD_CONFIRMATION_ACTIONS } = require("../../../src/includes/pageInside/sync/passwordConfirmation.js");
const h = React.createElement;

const DEFAULT_OPTIONS = {
  newOnly: false, updateSynced: true, variants: true, images: true,
  prices: true, stock: true, categories: true, filters: true, skipErrors: true,
};

const DropshippingSync = () => {
  const { api, t } = useCJDropshipping();
  const service = React.useMemo(() => createCJSyncService(api), [api]);
  const [modal, setModal] = React.useState({ show: false, mode: null, loading: false, error: null });
  const [preview, setPreview] = React.useState(null);
  const [summary, setSummary] = React.useState(null);
  const [options, setOptions] = React.useState(DEFAULT_OPTIONS);

  const closeModal = () => { if (!modal.loading) setModal({ show: false, mode: null, loading: false, error: null }); };
  const openPreview = async () => {
    setModal({ show: true, mode: "sync-all", loading: true, error: null });
    try {
      const data = await api.getSyncAllPreview();
      setPreview(data.data || data);
      setModal({ show: true, mode: "sync-all", loading: false, error: null });
    } catch (error) {
      setModal({ show: true, mode: "sync-all", loading: false, error });
    }
  };
  const run = async ({ confirmationToken }) => {
    if (modal.loading) return;
    setModal((state) => ({ ...state, loading: true, error: null }));
    try {
      const result = await service.syncAllCJProducts(options, { confirmationToken, confirmationAction: PASSWORD_CONFIRMATION_ACTIONS.CJ_SYNC_ALL });
      const nextSummary = result.data || result.summary;
      setSummary(nextSummary);
      setModal({ show: true, mode: "result", loading: false, error: null });
    } catch (error) {
      setModal((state) => ({ ...state, loading: false, error }));
    }
  };
  const toggle = (key) => setOptions((state) => ({ ...state, [key]: !state[key] }));
  const counts = preview?.summary || {};
  const invalid = !counts.total || (!options.newOnly && !options.updateSynced) || (!options.variants && !options.images && !options.prices && !options.stock && !options.categories && !options.filters);

  return h(React.Fragment, null,
    h(PageHeader, {
      title: t("h_cj_sync_title", "CJ synchronization"),
      description: t("h_cj_sync_desc", "Update prices, stock, images and availability for imported products."),
      action: h(Button, { onClick: openPreview }, t("h_cj_sync_all", "Sync all")),
    }),
    h("div", { className: "cjds-alert" }, t("h_cj_sync_schedule", "Automatic synchronization runs every six hours on the Elakay server.")),
    summary ? h("div", { className: "cjds-kpis" },
      h(Kpi, { value: summary.total || 0, label: t("h_cj_total", "Total") }),
      h(Kpi, { value: summary.completed ?? summary.synced ?? 0, label: t("h_cj_synced", "Completed") }),
      h(Kpi, { value: summary.skipped || 0, label: t("h_cj_skipped", "Skipped") }),
      h(Kpi, { value: summary.failed || 0, label: t("h_cj_failed", "Failed") })
    ) : null,
    h(CJSyncActionModal, {
      show: modal.show, mode: modal.mode || "sync-all", onHide: closeModal,
      title: modal.mode === "result" ? t("h_cj_sync_result_title", "Synchronization result") : t("h_cj_sync_all_title", "Synchronize all CJ products"),
      subtitle: modal.mode === "result"
        ? t("h_cj_sync_result_subtitle", "{completed} of {total} products synchronized", { completed: summary?.completed ?? summary?.synced ?? 0, total: summary?.total || 0 })
        : t("h_cj_sync_all_subtitle", "Review the scope and options before starting."),
      loading: modal.loading, error: modal.error, onConfirm: run,
      passwordRequired: modal.mode !== "result", confirmDisabled: invalid,
      confirmationAction: PASSWORD_CONFIRMATION_ACTIONS.CJ_SYNC_ALL,
      onPasswordConfirm: ({ password, action }) => api.confirmPassword(password, action),
    }, modal.mode === "result"
      ? h("div", null,
          h("p", null, t("h_cj_sync_result_summary", "Completed: {completed} · Skipped: {skipped} · Failed: {failed}", { completed: summary?.completed ?? summary?.synced ?? 0, skipped: summary?.skipped || 0, failed: summary?.failed || 0 })),
          (summary?.results || []).map((item) => h("div", { key: item.productId, className: `cj-sync-result cj-sync-result--${item.status}` }, `${item.title || item.productId}: ${item.status}${item.message ? ` — ${item.message}` : ""}`)))
      : h(React.Fragment, null,
          h("div", { className: "cjds-kpis" },
            h(Kpi, { value: counts.total || 0, label: t("h_cj_sync_preview_products", "Products") }), h(Kpi, { value: counts.variants || 0, label: t("h_cj_sync_preview_variants", "Variants") }),
            h(Kpi, { value: counts.conflicts || 0, label: t("h_cj_sync_preview_conflicts", "Conflicts") }), h(Kpi, { value: counts.skipped || 0, label: t("h_cj_sync_preview_will_skip", "Will be skipped") })
          ),
          h("div", { className: "cj-sync-options" }, Object.entries({
            newOnly: t("h_cj_sync_opt_new_only", "Synchronize only new products"), updateSynced: t("h_cj_sync_opt_update_synced", "Update synchronized products"), variants: t("h_cj_sync_opt_variants", "Synchronize variants"),
            images: t("h_cj_sync_opt_images", "Synchronize images"), prices: t("h_cj_sync_opt_prices", "Synchronize prices"), stock: t("h_cj_sync_opt_stock", "Synchronize stock"), categories: t("h_cj_sync_opt_categories", "Synchronize categories"),
            filters: t("h_cj_sync_opt_filters", "Synchronize filters"), skipErrors: t("h_cj_sync_opt_skip_errors", "Skip products containing errors"),
          }).map(([key, label]) => h("label", { key }, h("input", { type: "checkbox", checked: options[key], disabled: modal.loading, onChange: () => toggle(key) }), ` ${label}`))),
          counts.missingCategory ? h("div", { className: "cj-sync-action-modal__warning" }, t("h_cj_sync_missing_category", "{count} product(s) have no CJ category.", { count: counts.missingCategory })) : null,
          counts.invalidPrice ? h("div", { className: "cj-sync-action-modal__warning" }, t("h_cj_sync_invalid_price", "{count} product(s) have no valid supplier price.", { count: counts.invalidPrice })) : null
        )
    )
  );
};
module.exports = { DropshippingSync };
